import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Save,
  Building2,
  X,
  FileText,
  ChevronDown,
  ChevronUp,
  Check,
  Shield,
  CreditCard,
  Truck,
  Ship,
  Plane,
  Package,
  Users,
  AlertCircle,
  CheckCircle2,
  ArrowLeft,
  Hash,
  MapPin,
  Plus,
  Send,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { Input } from "../ui/input";
import { SearchBar } from "../SearchBox/SearchBar";

import {
  getAddressByID,
  DeleteCompanyAddress,
  createAddressBook,
  UpdateAddressBook,
  createAddressBookAddress,
  UpdateAddressBookAddress,
  getCPPositionList,
  getSelectedAccountAddress,
  DocUpdatebyAddressbook,
  InsertComplianceCertification,
  GetComplianceCertificationByID,
  GetCompanyAddressAdditionalDetails,
  UpdateCompanyAddressAdditionalDetails,
  getCheckDuplicateCompanyist,
  CreateUpdateCarrierContracts,
  getCarrierContractsByCompanyid,
} from "@/redux/Address/actions";

import { toast } from "sonner";
import { motion } from "framer-motion";
import { validateForm } from "../ui/useFormValidation";
import { callLoader } from "@/redux/Loader/actions";
import AddressBookAccount from "./AddressBookAccount";
import { showToast } from "@/redux/Toast/actions";
import CelebrationButton from "../ui/CelebrationButton/CelebrationButton";
import ModeConfigTab from "./ModeConfigTab";
import {
  addressMap,
  companyMap,
  entityType,
  generateAddressPayload,
  generateCompanyPayload,
  initialaddress,
  initialComplianceData,
  initialFormData,
  initialModeData,
  setComplianceData_,
  toFormBoolean,
  getAddressMapValue,
  validationConfig,
} from "./helper";
import ModesOfTransportation from "./ModesOfTransportation";
import CustomerDetailsSection from "./CustomerDetailsSection";
import AddressDetailsSection from "./AddressDetailsSection";
import PccDocumentationSection from "./PccDocumentationSection";
import AddressListSection from "./AddressListSection";
import ComplianceCertificationSection from "./ComplianceCertificationSection";
import SectionHeader from "./SectionHeader";
import PageHeader from "../FormHeader/formPageHeader";

/* ─── UI helpers (mirrors PurchaseOrderFormSleek) ─── */
const InputField = ({ label, required, children, className = "" }) => (
  <div className={className}>
    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const SECTION_COLORS = {
  blue: "bg-blue-500",
  teal: "bg-teal-500",
  violet: "bg-violet-500",
  cyan: "bg-cyan-500",
  green: "bg-emerald-500",
  indigo: "bg-indigo-500",
  amber: "bg-amber-500",
  orange: "bg-orange-500",
  rose: "bg-rose-500",
  dark: "bg-slate-700",
};

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT — all logic identical to original
══════════════════════════════════════════════════════════ */
const AddressForm = ({ onBack, bookingData: initialBookingData = null }) => {
  const [bookingData, setBookingData] = useState(initialBookingData);
  const dispatch = useDispatch();
  const { countryList, salesPersonList, standardTermsList } = useSelector(
    (state) => state.common,
  );
  const { isAnimate } = useSelector((state) => state.toast);
  const [showBox, setShowBox] = useState(false);
  const [dateErrors, setDateErrors] = useState({});
  const authUser = JSON.parse(localStorage.getItem("authUser"))?.[0];
  const user = authUser?.[0];
  const _companyId = authUser?.companyId;// company id used in duplicate checking data
  const [isEditMode, setIsEditMode] = useState(false);
  const [curriercontractcompanyid, setCurriercontractcompanyid] = useState(0);

  const salesOptions = salesPersonList?.map((s) => ({
    value: s.salesPersonId,
    label: s.salesPerson,
  }));
  const [formData, setFormData] = useState({ ...initialFormData });

  const [modeConfigData, setModeConfigData] = useState({
    OCEAN_IMPORT: { ...initialModeData },
    OCEAN_EXPORT: { ...initialModeData },
    AIR_IMPORT: { ...initialModeData },
    AIR_EXPORT: { ...initialModeData },
    DOMESTIC_TOWING: { ...initialModeData },
    WAREHOUSING: { ...initialModeData },
    DISTRIBUTION: { ...initialModeData },
  });

  const handleModeDataChange = (modeId, field, value, label) => {
    if (field === "paymentTermsId") {
      setModeConfigData((prev) => ({
        ...prev,
        [modeId]: { ...prev[modeId], [field]: value, paymentTerms: label },
      }));
    } else if (field === "accessControlsBookingApprovalRequiredId") {
      setModeConfigData((prev) => ({
        ...prev,
        [modeId]: {
          ...prev[modeId],
          [field]: value,
          accessControlsBookingApprovalRequired: label,
        },
      }));
    } else if (field === "accessControlsCreditOverrideApprovalId") {
      setModeConfigData((prev) => ({
        ...prev,
        [modeId]: {
          ...prev[modeId],
          [field]: value,
          accessControlsCreditOverrideApproval: label,
        },
      }));
    } else {
      setModeConfigData((prev) => ({
        ...prev,
        [modeId]: { ...prev[modeId], [field]: value },
      }));
    }
  };

  const [Addresses, setAddresses] = useState([]);

  useEffect(() => {
    if (bookingData != 0 && bookingData != null) {
      _getAddressBook();
      setIsEditMode(true);
    }
    _getdataCpPositionList();
  }, [bookingData]);

  const loadCarrierContracts = async (companyId) => {
    try {
      const res = await dispatch(getCarrierContractsByCompanyid(companyId));
      const contracts = res.map((item) => item.contractNumber);
      setCarrierList(contracts);
    } catch (error) {
      console.error("Error loading contracts:", error);
    }
  };

  const _getAddressBook = async () => {
    try {
      dispatch(callLoader(1));
      const resultData = await dispatch(getAddressByID(bookingData));
      const api = resultData?.payload || resultData;
      const companyId = api?._Company?.id;
      setCurriercontractcompanyid(companyId);
      if (companyId > 0) loadCarrierContracts(companyId);

      setFormData((prev) => {
        const updated = { ...prev };
        Object.keys(companyMap).forEach((key) => {
          updated[key] = api?._Company?.[companyMap[key]] ?? "";
        });
        updated.ddlsalesperson = api?._Company?.salespersonids
          ? api?._Company?.salespersonids.split(",").map(Number)
          : [];
        const currentAddress = Array.isArray(api?.companyAddress)
          ? api.companyAddress[0]
          : api?.companyAddress;
        Object.keys(addressMap).forEach((key) => {
          const raw = getAddressMapValue(currentAddress, addressMap[key]);
          updated[key] = key.startsWith("chk")
            ? toFormBoolean(raw)
            : (raw ?? "");
        });
        return updated;
      });
      setAddresses(
        Array.isArray(api?.companyAddress)
          ? api.companyAddress
          : api?.companyAddress
            ? [api.companyAddress]
            : [],
      );
    } catch (error) {
    } finally {
      dispatch(callLoader(0));
    }
  };

  const _getdataCpPositionList = async () => {
    try {
      dispatch(callLoader(1));
      try {
        const data = await getCPPositionList();
        if (Array.isArray(data.data)) setCpPositionList(data.data);
        else setCpPositionList([]);
      } catch (error) {
        setCpPositionList([]);
      }
    } catch (error) {
    } finally {
      dispatch(callLoader(0));
    }
  };

  const _setNewAddressBook = async () => {
    setFormData();
  };

  const [errors, setErrors] = useState({});
  const [items, setItems] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    s1: true,
    s1b: true,
    s2: true,
    s3: true,
    s4: false,
    s5: true,
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const [duplicateCompanies, setDuplicateCompanies] = useState([]);
  const [duplicateStatus, setDuplicateStatus] = useState("idle");
  const [lastCheckedName, setLastCheckedName] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const duplicateTimerRef = useRef(null);
  const duplicateCheckRequestRef = useRef(0);
  const editingCompanyIdRef = useRef(0);
  editingCompanyIdRef.current =
    curriercontractcompanyid || formData?.companyID || 0;

  const normalizeDuplicateList = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.companies)) return data.companies;
    if (Array.isArray(data?.list)) return data.list;
    return [];
  };

  const runDuplicateCheck = useCallback(
    async (companyName) => {
      const trimmed = companyName?.trim();
      if (!trimmed) {
        setDuplicateCompanies([]);
        setDuplicateStatus("idle");
        setLastCheckedName("");
        setShowDropdown(false);
        return { hasDuplicates: false, checked: true };
      }

      const requestId = ++duplicateCheckRequestRef.current;
      setDuplicateStatus("checking");

      try {
        const checkPromise = dispatch(
          getCheckDuplicateCompanyist({
            companyName: trimmed,
            masterCompanyId: _companyId,
          }),
        );
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(
            () => reject(new Error("Duplicate check timed out")),
            20000,
          );
        });
        const data = await Promise.race([checkPromise, timeoutPromise]);

        if (requestId !== duplicateCheckRequestRef.current) {
          return { hasDuplicates: false, checked: false, stale: true };
        }

        const rawList = normalizeDuplicateList(data);
        const editingCompanyId = editingCompanyIdRef.current;
        const list = editingCompanyId
          ? rawList.filter(
              (item) => Number(item.id) !== Number(editingCompanyId),
            )
          : rawList;
        const hasDuplicates = list.length > 0;
        setDuplicateCompanies(list);
        setLastCheckedName(trimmed);
        setDuplicateStatus(hasDuplicates ? "found" : "clear");
        setShowDropdown(hasDuplicates);
        return { hasDuplicates, checked: true };
      } catch (err) {
        if (requestId !== duplicateCheckRequestRef.current) {
          return { hasDuplicates: false, checked: false, stale: true };
        }
        console.error(err?.message || err);
        setDuplicateStatus("error");
        setDuplicateCompanies([]);
        setShowDropdown(false);
        return { hasDuplicates: false, checked: false, error: true };
      }
    },
    [dispatch, _companyId],
  );

  const runDuplicateCheckRef = useRef(runDuplicateCheck);
  runDuplicateCheckRef.current = runDuplicateCheck;

  useEffect(() => {
    const trimmed = formData.companyname?.trim();
    if (!trimmed) {
      setDuplicateStatus("idle");
      setDuplicateCompanies([]);
      setLastCheckedName("");
      setShowDropdown(false);
      return;
    }

    if (trimmed === lastCheckedName) return;

    if (duplicateTimerRef.current) clearTimeout(duplicateTimerRef.current);
    duplicateTimerRef.current = setTimeout(() => {
      runDuplicateCheckRef.current(trimmed);
    }, 500);

    return () => {
      if (duplicateTimerRef.current) clearTimeout(duplicateTimerRef.current);
    };
  }, [formData.companyname, lastCheckedName]);

  const duplicateMessage = useMemo(() => {
    switch (duplicateStatus) {
      case "checking":
        return "Checking for duplicate company names...";
      case "clear":
        return "No duplicate found. You can submit.";
      case "found":
        return "Duplicate company name found. Please enter a unique name to continue.";
      case "error":
        return "Could not verify company name. Please try again.";
      default:
        return "";
    }
  }, [duplicateStatus]);

  const handleCheckDuplicate = async () => {
    if (!formData.companyname?.trim()) {
      dispatch(showToast("Please enter a company name first.", "error"));
      return;
    }
    if (duplicateTimerRef.current) clearTimeout(duplicateTimerRef.current);
    await runDuplicateCheck(formData.companyname);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
      ...(name === "companyname" && {
        companyname2: value,
        ...(value?.trim() !== lastCheckedName ? { id: "" } : {}),
      }),
    }));
    if (name === "companyname") {
      const trimmed = value?.trim() ?? "";
      setShowDropdown(false);

      if (!trimmed) {
        setDuplicateStatus("idle");
        setDuplicateCompanies([]);
      } else if (trimmed !== lastCheckedName) {
        setDuplicateStatus("idle");
        setDuplicateCompanies([]);
      }
    }
  };

  const handleSubmit = async (e) => {
    if (!isAnyChecked) {
      dispatch(showToast("Please select at least one Entity type", "error"));
      return;
    }

    if (formData.companyname?.trim()) {
      if (duplicateStatus === "checking") {
        dispatch(
          showToast(
            "Please wait until duplicate check finishes before submitting.",
            "error",
          ),
        );
        return;
      }

      const nameMatchesLastCheck =
        lastCheckedName === formData.companyname.trim();
      if (!nameMatchesLastCheck || duplicateStatus === "idle") {
        const result = await runDuplicateCheck(formData.companyname);
        if (result.error) {
          dispatch(
            showToast(
              "Could not verify company name. Please try again.",
              "error",
            ),
          );
          return;
        }
        if (result.hasDuplicates) {
          dispatch(
            showToast(
              "This company name already exists. Please enter a unique name.",
              "error",
            ),
          );
          return;
        }
      } else if (duplicateStatus === "found") {
        dispatch(
          showToast(
            "This company name already exists. Please enter a unique name.",
            "error",
          ),
        );
        return;
      }
    }
    const finalPayload = {
      _Company: generateCompanyPayload(
        bookingData,
        authUser,
        salesPersonList,
        formData,
      ),
      Company_Address: generateAddressPayload(bookingData, authUser, formData),
    };
    const validationErrors = validateForm({
      data: formData,
      items,
      rules: validationConfig,
    });
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    try {
      dispatch(callLoader(1));
      if (bookingData != 0 && bookingData != null) {
        const updateDetails = await UpdateAddressBook(finalPayload);
        if (
          updateDetails?.statuscode === 200 ||
          updateDetails?.statuscode === 201
        ) {
          dispatch(showToast("Record Update successfully", "success", true));
        }
      } else {
        const addDetails = await createAddressBook(finalPayload);
        if (addDetails?.statuscode === 201 || addDetails?.statuscode === 200) {
          setBookingData(addDetails?.addressBookId);
          dispatch(showToast("Record Save successfully", "success", true));
        }
      }
      if (carrierList.length > 0 && finalPayload._Company.id > 0) {
        const payloadCurriercontract = {
          companyID: finalPayload._Company.id,
          loggedinUser: authUser.username,
          contracts: carrierList.map((item) => ({ contractNumber: item })),
        };
        await CreateUpdateCarrierContracts(payloadCurriercontract);
      }
    } catch (error) {
      dispatch(
        showToast(
          bookingData?.id
            ? "Failed to update company address."
            : "Failed to add company address.",
          "error",
        ),
      );
    } finally {
      dispatch(callLoader(0));
    }
  };

  const addItem = (key) => {
    const { id, input, list = [] } = formData[key];
    if (!id) return;
    if (list.some((item) => item.id === id)) return;
    setFormData((prev) => ({
      ...prev,
      [key]: { input: "", id: "", list: [...list, { id, name: input }] },
    }));
  };

  const removeItem = (key, id) => {
    setFormData((prev) => ({
      ...prev,
      [key]: { ...prev[key], list: prev[key].list.filter((i) => i.id !== id) },
    }));
  };

  const SelectedList = ({ title, items = [], onRemove }) => {
    if (!items.length) return null;
    return (
      <div className="mt-4 border border-slate-300 dark:border-slate-700 rounded-lg p-3">
        <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">
          {title}
        </h4>
        <div className="flex flex-wrap gap-2">
          {items.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-2 px-3 py-1 text-sm bg-slate-100 dark:bg-slate-700 rounded-full"
            >
              <span>{item.name}</span>
              <button
                type="button"
                onClick={() => onRemove(item.id)}
                className="text-red-500 hover:text-red-700"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const { addressList, pagination } = useSelector((state) => state.addressBook);

  const MultiSearchAdd = ({
    label,
    valueKey,
    searchAPI,
    formData,
    setFormData,
  }) => {
    const data = formData[valueKey] || { input: "", id: "", list: [] };
    return (
      <div>
        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
          {label}:
        </label>
        <div className="mt-4 border border-slate-300 dark:border-slate-700 rounded-lg p-3">
          <div className="flex items-stretch gap-2">
            <div className="flex-1">
              <SearchBar
                searchAPI={searchAPI}
                value={data.input}
                selectReacord={(d) => {
                  setFormData((prev) => ({
                    ...prev,
                    [valueKey]: {
                      ...prev[valueKey],
                      input: d?.[1],
                      id: d?.[0],
                    },
                  }));
                }}
              />
            </div>
            <button
              type="button"
              onClick={() => addItem(valueKey)}
              className="px-3 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              +
            </button>
          </div>
        </div>
        <SelectedList
          title={`Selected ${label}`}
          items={data?.list || []}
          onRemove={(id) => removeItem(valueKey, id)}
        />
      </div>
    );
  };

  const handleDateChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isAnyChecked = Object.entries(formData)
    .filter(([key]) => key.startsWith("chk"))
    .some(([, value]) => value === true);

  const handleSubmitAddress = async (e) => {
    if (!isAnyChecked) {
      toast.error("Please select at least one Entity type");
      return;
    }
    const validationErrors = validateForm({
      data: formData,
      items,
      rules: validationConfig,
    });
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;
    const isEdit = Boolean(bookingData && formData.addressIdSelected);
    if (isEdit) {
      const addressPayload = generateAddressPayload(
        bookingData,
        authUser,
        formData,
      );
      const finalPayload = {
        _Company: generateCompanyPayload(
          bookingData,
          authUser,
          salesPersonList,
          formData,
        ),
        Company_Address: {
          ...addressPayload,
          addressid: formData.addressIdSelected,
        },
      };
      dispatch(callLoader(1));
      const updateDetailsAddress = await UpdateAddressBookAddress(finalPayload);
      dispatch(callLoader(0));
      if (
        updateDetailsAddress?.statuscode === 200 ||
        updateDetailsAddress?.statuscode === 201
      ) {
        toast.success("Address Update successfully");
        _getAddressBook();
      } else {
        toast.error("Address update failed. Please try again.");
      }
    } else {
      const addressPayload = generateAddressPayload(
        bookingData,
        authUser,
        formData,
      );
      const finalPayload = { ...addressPayload };
      const addDetailsAddress = await createAddressBookAddress(finalPayload);
      if (
        addDetailsAddress?.statuscode === 200 ||
        addDetailsAddress?.statuscode === 201
      ) {
        toast.success("Address Save successfully");
        _getAddressBook();
      }
    }
  };

  const handleDateError = (fieldName, hasError) => {
    setDateErrors((prev) => ({ ...prev, [fieldName]: hasError }));
  };

  const handleAddressSelect = (addressId) => {
    const selectedAddress = Addresses.find(
      (addr) => addr.addressid === addressId,
    );
    if (!selectedAddress) return;
    setFormData((prev) => ({ ...prev, addressIdSelected: addressId }));
    fillAddressForm(selectedAddress);
  };

  const fillAddressForm = (address) => {
    setFormData((prev) => {
      const updated = { ...prev };
      Object.keys(addressMap).forEach((key) => {
        const raw = getAddressMapValue(address, addressMap[key]);
        updated[key] = key.startsWith("chk")
          ? toFormBoolean(raw)
          : (raw ?? "");
      });
      return updated;
    });
  };

  const ResetAddressDetail = () => {
    setFormData((prev) => ({
      ...prev,
      ...initialaddress,
      companyname2: formData.companyname,
    }));
    setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    const updateDetails = await DeleteCompanyAddress(id);
    if (
      updateDetails?.statuscode === 201 ||
      updateDetails?.statuscode === 200
    ) {
      toast.success("Address delete successfully");
      _getAddressBook();
    }
  };

  const [carrierInput, setCarrierInput] = useState("");
  const [carrierContracts, setCarrierContracts] = useState([]);

  const addCarrierContract = () => {
    if (!carrierInput.trim()) return;
    if (carrierContracts.includes(carrierInput.trim())) return;
    setCarrierContracts((prev) => [...prev, carrierInput.trim()]);
    setCarrierInput("");
  };

  const removeCarrierContract = (value) => {
    setCarrierContracts((prev) => prev.filter((item) => item !== value));
  };

  const [cpPositionList, setCpPositionList] = useState([]);


  function getModeTabs() {
    const modeTabs = [];
    if (formData?.OCEAN_IMPORT)
      modeTabs.push({
        id: "OCEAN_IMPORT",
        label: "Ocean Import",
        icon: Ship,
        color: "text-blue-500",
      });
    if (formData?.OCEAN_EXPORT)
      modeTabs.push({
        id: "OCEAN_EXPORT",
        label: "Ocean Export",
        icon: Ship,
        color: "text-blue-600",
      });
    if (formData?.AIR_IMPORT)
      modeTabs.push({
        id: "AIR_IMPORT",
        label: "Air Import",
        icon: Plane,
        color: "text-purple-500",
      });
    if (formData?.AIR_EXPORT)
      modeTabs.push({
        id: "AIR_EXPORT",
        label: "Air Export",
        icon: Plane,
        color: "text-purple-600",
      });
    if (formData?.DOMESTIC_TOWING)
      modeTabs.push({
        id: "DOMESTIC_TOWING",
        label: "Domestic/Trucking",
        icon: Truck,
        color: "text-amber-500",
      });
    if (formData?.WAREHOUSING)
      modeTabs.push({
        id: "WAREHOUSING",
        label: "Warehousing",
        icon: Package,
        color: "text-emerald-500",
      });
    if (formData?.DISTRIBUTION)
      modeTabs.push({
        id: "DISTRIBUTION",
        label: "Distribution",
        icon: Users,
        color: "text-rose-500",
      });
    return modeTabs;
  }

  const modeTabs = getModeTabs();

  const coreTabs = [
    {
      id: "company",
      label: "Company Info",
      icon: Building2,
      alwaysEnabled: true,
      color: "blue",
    },
    {
      id: "documentation",
      label: "Docs / Special Notes",
      icon: FileText,
      alwaysEnabled: false,
      color: "cyan",
    },
    {
      id: "compliance",
      label: "Compliance / Cert.",
      icon: Shield,
      alwaysEnabled: false,
      color: "violet",
    },
    {
      id: "accounting",
      label: "Accounting",
      icon: CreditCard,
      alwaysEnabled: false,
      color: "amber",
    },
  ];

  const tabs = [
    ...coreTabs,
    ...modeTabs.map((m) => ({ ...m, alwaysEnabled: false })),
  ];

  const [activeTab, setActiveTab] = useState("company");

  /* shared input/select css */
  const inputClass =
    "w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all";
  const selectClass =
    "w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all appearance-none cursor-pointer";

  const RefreshPrintAddress = () => {
    setFormData((prev) => {
      let addressLines = [
        prev.companyname2 || "",
        prev.address1 || "",
        prev.address2 || "",
        prev.address3 || "",
        `${prev.city || ""} ${prev.state || ""} ${prev.postalcode || ""} ${prev.province || ""} ${prev.country || ""}`.trim(),
        (() => {
          let contactLine = "";
          if (prev.officetel?.trim())
            contactLine += "Tel: " + prev.officetel.trim();
          if (prev.fax?.trim())
            contactLine += contactLine
              ? " Fax: " + prev.fax.trim()
              : "Fax: " + prev.fax.trim();
          return contactLine;
        })(),
        prev.email || "",
      ].filter(Boolean);
      if (prev.eorinumber?.trim()) {
        let lastIndex = addressLines.length - 1;
        addressLines[lastIndex] += " EORI #: " + prev.eorinumber.trim();
      }
      return {
        ...prev,
        printaddress: addressLines.join("\n"),
        printaddressaccount: addressLines.join("\n"),
      };
    });
  };

  const [DocumentFormData, setDocumentFormData] = useState({
    ADDRESSID: formData.addressId || 0,
    specialnotesimport: formData.specialnotesimport || "",
    specialnotesexport: formData.specialnotesexport || "",
  });

  const handleDocumentInputChange = (e) => {
    const { name, value } = e.target;
    setDocumentFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (activeTab === "documentation") {
      const data = dispatch(getAddressByID(bookingData)).then((data) => {
        setDocumentFormData({
          ADDRESSID: bookingData || 0,
          specialnotesexport: data._Company.specialnotesexport || "",
          specialnotesimport: data._Company.specialnotesimport || "",
        });
      });
    }
  }, [activeTab, dispatch, bookingData]);

  const handleDocSaveOrUpdate = async () => {
    try {
      if (DocumentFormData.ADDRESSID > 0) {
        dispatch(callLoader(1));
        await DocUpdatebyAddressbook(DocumentFormData);
        dispatch(
          showToast(
            "Updated Address book Document successfully",
            "success",
            true,
          ),
        );
      }
    } catch (error) {
      dispatch(showToast("Something went wrong", "error"));
    } finally {
      dispatch(callLoader(0));
    }
  };

  const [complianceData, setComplianceData] = useState({
    ...initialComplianceData,
    custID: formData.companyID || 0,
  });

  useEffect(() => {
    if (activeTab === "compliance" && formData.companyID) {
      dispatch(GetComplianceCertificationByID(formData.companyID))
        .then((data) => {
          if (data) {
            const complianceData_ = setComplianceData_(data);
            setComplianceData({ ...complianceData_ });
          }
        })
        .catch((err) => {
          console.error("Error fetching compliance data:", err);
        });
    }
  }, [activeTab, dispatch, bookingData]);

  const CompliancehandleSubmit = async () => {
    dispatch(callLoader(1));
    try {
      complianceData.custID = formData.companyID;
      await InsertComplianceCertification(complianceData);
      dispatch(
        showToast("Compliance data submitted successfully!", "success", true),
      );
    } catch (error) {
      console.error(error);
      dispatch(showToast("Something went wrong", "error"));
    } finally {
      dispatch(callLoader(0));
    }
  };

  const CompliancehandleChange = (key, value) => {
    setComplianceData((prev) => ({ ...prev, [key]: value }));
  };

  const _GetCompanyAddressAdditionalDetails = async (mode) => {
    try {
      dispatch(callLoader(1));
      const resultData = await dispatch(
        GetCompanyAddressAdditionalDetails(formData?.addressId, mode),
      );
      setModeConfigData((pre) => ({
        ...pre,
        [mode]: {
          ...initialModeData,
          ...resultData,
          addressId: formData?.addressId,
        },
      }));
    } catch (error) {
      setModeConfigData((pre) => ({ ...pre, [mode]: { ...initialModeData } }));
    } finally {
      dispatch(callLoader(0));
    }
  };

  useEffect(() => {
    const isIncluded = Object.keys(modeConfigData).some((key) =>
      key.includes(activeTab),
    );
    if (isIncluded) _GetCompanyAddressAdditionalDetails(activeTab);
  }, [activeTab]);

  const handleSaveModeConfig = async (modeId) => {
    const authUser = JSON.parse(localStorage.getItem("authUser"))?.[0];
    const payload = modeConfigData?.[modeId];
    payload.modeType = modeId;
    payload.addressId = formData?.addressId;
    payload.companyId = formData?.companyID;
    payload.createdBy = authUser?.username || "System";
    payload.isActive = true;
    try {
      dispatch(callLoader(1));
      if (bookingData != 0 && bookingData != null) {
        const updateDetails =
          await UpdateCompanyAddressAdditionalDetails(payload);
        if (
          updateDetails?.statuscode === 200 ||
          updateDetails?.statuscode === 201
        ) {
          dispatch(showToast("Record Update successfully", "success", true));
        }
      }
    } catch (error) {
      dispatch(showToast("Something went wrong. Please try again.", "error"));
    } finally {
      dispatch(callLoader(0));
    }
  };

  const [carrierList, setCarrierList] = useState([]);

  const handleAddCarrier = () => {
    const value = formData.carriercontract?.trim();
    if (!value) return;
    if (carrierList.includes(value)) return;
    setCarrierList((prev) => [...prev, value]);
    setFormData((prev) => ({ ...prev, carriercontract: "" }));
  };

  const handleDeleteCarrier = (index) => {
    setCarrierList((prev) => prev.filter((_, i) => i !== index));
  };

  /* ── inline SectionHeader (PO style) ── */
  const SectionHeader = ({
    icon: Icon,
    title,
    color = "blue",
    section,
    badge,
    action,
  }) => {
    const iconBg = SECTION_COLORS[color] ?? SECTION_COLORS.blue;
    return (
      <button
        onClick={() => toggleSection(section)}
        className="w-full flex items-center justify-between p-4 border-b bg-gradient-to-r to-slate-100 dark:from-slate-800 dark:to-slate-700 cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg shadow-md ${iconBg}`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-200">
            {title}
          </h2>
          {badge && (
            <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-xs font-medium rounded-full">
              {badge}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {action}
          <div
            className={`p-1.5 rounded-md transition-colors ${expandedSections[section] ? "bg-indigo-100 dark:bg-indigo-900/30" : "bg-slate-200 dark:bg-slate-700"}`}
          >
            {expandedSections[section] ? (
              <ChevronUp className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-500 dark:text-slate-400" />
            )}
          </div>
        </div>
      </button>
    );
  };

  /* ── tab color map ── */
  const TAB_ACTIVE_COLOR = {
    company: "bg-blue-600 text-white shadow-sm",
    documentation: "bg-cyan-600 text-white shadow-sm",
    compliance: "bg-violet-600 text-white shadow-sm",
    accounting: "bg-amber-600 text-white shadow-sm",
    OCEAN_IMPORT: "bg-blue-500 text-white shadow-sm",
    OCEAN_EXPORT: "bg-blue-600 text-white shadow-sm",
    AIR_IMPORT: "bg-purple-500 text-white shadow-sm",
    AIR_EXPORT: "bg-purple-600 text-white shadow-sm",
    DOMESTIC_TOWING: "bg-amber-500 text-white shadow-sm",
    WAREHOUSING: "bg-emerald-500 text-white shadow-sm",
    DISTRIBUTION: "bg-rose-500 text-white shadow-sm",
  };

  /* stepper fill detection */
  const isCoreTabFilled = useCallback(
    (id) => {
      switch (id) {
        case "company":
          return !!(formData?.companyname || formData?.ddlofficetype);
        case "documentation":
          return isEditMode;
        case "compliance":
          return isEditMode;
        case "accounting":
          return isEditMode;
        default:
          return false;
      }
    },
    [formData, isEditMode],
  );

  /* ══════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════ */
  return (
    <div className="h-full bg-slate-50 dark:bg-slate-950 sticky">
      {/* ── STICKY PAGE HEADER ── */}
      <div className="page-header-sticky sticky top-0 z-50">
        <PageHeader
          icon={<Building2 className="w-5 h-5 text-white" />}
          title="Address Book"
          subtitle={
            bookingData ? (
              <>
                <span>Editing </span>
                <span className="font-semibold text-slate-700 dark:text-slate-300">
                  {formData?.companyname || "Company"}
                </span>
              </>
            ) : (
              "Create a new company address record"
            )
          }
          actions={[
            {
              label: "Back",
              icon: <ArrowLeft className="w-4 h-4" />,
              onClick: onBack,
              variant: "ghost",
            },
          ]}
          onSubmit={handleSubmit}
          submitLabel={bookingData ? "Submit Company" : "Submit Company"}
          isSuccess={isAnimate}
        />
        <div style={{ height: "var(--page-header-height)" }} />
      </div>

      <div className="p-4 space-y-3 pb-20">



        {/* ── FULL TAB BAR (all tabs incl. mode tabs) ── */}
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 p-1.5">
          <div className="flex flex-wrap gap-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isDisabled = !isEditMode && !tab.alwaysEnabled;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  disabled={isDisabled}
                  onClick={() => !isDisabled && setActiveTab(tab.id)}
                  className={[
                    "px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5",
                    isActive
                      ? (TAB_ACTIVE_COLOR[tab.id] ??
                        "bg-blue-600 text-white shadow-sm")
                      : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800",
                    isDisabled && "opacity-40 cursor-not-allowed",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
        {/* <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 px-4 py-3 overflow-x-auto">
          <div className="flex items-center w-full justify-between gap-0">
            {coreTabs.map((tab, index) => {
              const isDisabled = !isEditMode && !tab.alwaysEnabled;
              const filled = isCoreTabFilled(tab.id);
              const done = filled && activeTab !== tab.id && !isDisabled;
              const active = activeTab === tab.id;
              return (
                <React.Fragment key={tab.id}>
                  <div
                    onClick={() => !isDisabled && setActiveTab(tab.id)}
                    className={[
                      "flex items-center gap-2 h-8 pl-2 pr-3 rounded-full text-xs font-medium transition-all duration-200 select-none",
                      isDisabled
                        ? "opacity-40 cursor-not-allowed"
                        : "cursor-pointer",
                      done &&
                        !isDisabled &&
                        "text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20",
                      active &&
                        "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300",
                      !done &&
                        !active &&
                        !isDisabled &&
                        "text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <span
                      className={[
                        "w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0",
                        done && !isDisabled && "bg-green-500 text-white",
                        active && "bg-blue-600 text-white",
                        !done &&
                          !active &&
                          "bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      {done && !isDisabled ? (
                        <Check className="w-3 h-3" />
                      ) : (
                        index + 1
                      )}
                    </span>
                    <span className="hidden sm:inline">{tab.label}</span>
                  </div>
                  {index < coreTabs.length - 1 && (
                    <div
                      className={[
                        "flex-1 h-0.5 min-w-[8px] mx-1 rounded-full transition-colors duration-300",
                        done && !isDisabled
                          ? "bg-green-400 dark:bg-green-600"
                          : "bg-slate-200 dark:bg-slate-700",
                      ].join(" ")}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div> */}

        {/* ══════════ COMPANY TAB ══════════ */}
        {activeTab === "company" && (
          <div className="space-y-3">
            {/* Entity Type */}
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
              <SectionHeader
                icon={Users}
                title="Entity Type"
                color="indigo"
                section="s1"
              />
              {expandedSections.s1 && (
                <div className="p-4">
                  <div className="flex flex-wrap gap-2">
                    {(entityType || []).map((entity) => (
                      <label
                        key={entity.name}
                        className={[
                          "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-all duration-200 select-none",
                          !!formData?.[entity.name]
                            ? "bg-blue-600 text-white shadow-sm hover:bg-blue-700"
                            : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700",
                        ].join(" ")}
                      >
                        <Input
                          type="checkbox"
                          className="sr-only"
                          value={formData?.[entity.name]}
                          checked={!!formData?.[entity.name]}
                          onChange={handleInputChange}
                          name={entity.name}
                        />
                        {!!formData?.[entity.name] && (
                          <Check className="w-3 h-3" />
                        )}
                        {entity.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modes of Transportation */}
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
              <ModesOfTransportation
                expandedSections={expandedSections}
                toggleSection={toggleSection}
                formData={formData}
                handleInputChange={handleInputChange}
              />
            </div>

            {/* Customer Details */}
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
              <CustomerDetailsSection
                expandedSections={expandedSections}
                onToggle={toggleSection}
                formData={formData}
                handleInputChange={handleInputChange}
                errors={errors}
                inputClass={inputClass}
                salesOptions={salesOptions}
                setFormData={setFormData}
                handleDateChange={handleDateChange}
                handleDateError={handleDateError}
                carrierList={carrierList}
                handleAddCarrier={handleAddCarrier}
                handleDeleteCarrier={handleDeleteCarrier}
                handleCheckDuplicate={handleCheckDuplicate}
                duplicateCompanies={duplicateCompanies}
                duplicateStatus={duplicateStatus}
                duplicateMessage={duplicateMessage}
                showDropdown={showDropdown}
                setShowDropdown={setShowDropdown}
              />
            </div>

            {/* Add New Address button */}
            {bookingData && (
              <div className="flex justify-end">
                <button
                  onClick={ResetAddressDetail}
                  type="button"
                  className="inline-flex items-center gap-1.5 px-4 h-9 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition-colors shadow-sm"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add New Address
                </button>
              </div>
            )}

            {/* Address Details */}
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
              <AddressDetailsSection
                expandedSections={expandedSections}
                onToggle={toggleSection}
                formData={formData}
                handleInputChange={handleInputChange}
                errors={errors}
                inputClass={inputClass}
                countryList={countryList}
                cpPositionList={cpPositionList}
                RefreshPrintAddress={RefreshPrintAddress}
                bookingData={bookingData}
                handleSubmitAddress={handleSubmitAddress}
              />
            </div>

            {/* Link To Entities (hidden unless showBox) */}
            <div
              className={`bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200 ${showBox ? "" : "hidden"}`}
            >
              <SectionHeader
                icon={Users}
                title="Link To Entities"
                color="teal"
                section="s4"
              />
              {expandedSections.s4 && (
                <div className="p-4">
                  <div className="grid grid-cols-2 gap-6 items-start">
                    <div className="grid grid-cols-1 gap-4">
                      <MultiSearchAdd
                        label="Shipper"
                        valueKey="shipper"
                        searchAPI="v1/AutoSuggestion/GetCustomerSuggestion?Prefix="
                        formData={formData}
                        setFormData={setFormData}
                      />
                      <MultiSearchAdd
                        label="Trucker"
                        valueKey="trucker"
                        searchAPI="v1/AutoSuggestion/GetCustomerSuggestion?Prefix="
                        formData={formData}
                        setFormData={setFormData}
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                      <MultiSearchAdd
                        label="Consignee"
                        valueKey="consignee"
                        searchAPI="v1/AutoSuggestion/GetCustomerSuggestion?Prefix="
                        formData={formData}
                        setFormData={setFormData}
                      />
                      <MultiSearchAdd
                        label="Carrier"
                        valueKey="carrier"
                        searchAPI="v1/AutoSuggestion/GetCarrierSuggestion?Prefix="
                        formData={formData}
                        setFormData={setFormData}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Address List */}
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
              <AddressListSection
                expandedSections={expandedSections}
                Addresses={Addresses || []}
                handleAddressSelect={handleAddressSelect}
                handleDelete={handleDelete}
              />
            </div>
          </div>
        )}

        {/* ══════════ DOCUMENTATION TAB ══════════ */}
        {activeTab === "documentation" && (
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
            <PccDocumentationSection
              DocumentFormData={DocumentFormData}
              handleDocumentInputChange={handleDocumentInputChange}
              handleSave={handleDocSaveOrUpdate}
              isAnimate={isAnimate}
              inputClass={inputClass}
            />
          </div>
        )}

        {/* ══════════ COMPLIANCE TAB ══════════ */}
        {activeTab === "compliance" && (
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
            <ComplianceCertificationSection
              complianceData={complianceData}
              CompliancehandleChange={CompliancehandleChange}
              CompliancehandleSubmit={CompliancehandleSubmit}
              isAnimate={isAnimate}
            />
          </div>
        )}

        {/* ══════════ ACCOUNTING TAB ══════════ */}
        {activeTab === "accounting" && (
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
            <AddressBookAccount
              bookingData={bookingData}
              standardTermsList={standardTermsList}
              activeTab={activeTab}
              onToggle={toggleSection}
              expandedSections={expandedSections}
            />
          </div>
        )}

        {/* ══════════ MODE TABS ══════════ */}
        {modeTabs.map(
          (modeTab) =>
            activeTab === modeTab.id && (
              <div
                key={modeTab.id}
                className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200"
              >
                <ModeConfigTab
                  modeId={modeTab.id}
                  modeLabel={modeTab.label}
                  ModeIcon={modeTab.icon}
                  modeColor={
                    modeTab.id.includes("OCEAN")
                      ? "from-blue-500 to-cyan-600"
                      : modeTab.id.includes("AIR")
                        ? "from-purple-500 to-violet-600"
                        : modeTab.id.includes("DOMESTIC")
                          ? "from-amber-500 to-orange-600"
                          : modeTab.id.includes("WAREHOUSING")
                            ? "from-emerald-500 to-green-600"
                            : "from-rose-500 to-pink-600"
                  }
                  modeData={modeConfigData?.[modeTab.id] || {}}
                  onModeDataChange={handleModeDataChange}
                  isAnimate={isAnimate}
                  onSave={() => handleSaveModeConfig(modeTab.id)}
                />
              </div>
            ),
        )}

        {/* Generic placeholder for undefined tabs */}
        {activeTab !== "company" &&
          activeTab !== "documentation" &&
          activeTab !== "compliance" &&
          activeTab !== "accounting" &&
          !modeTabs.some((modeTab) => modeTab.id === activeTab) && (
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden p-12 text-center">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                {tabs.find((t) => t.id === activeTab)?.label}
              </h3>
              <p className="text-slate-500 dark:text-slate-400">
                This section is under development
              </p>
            </div>
          )}

        {/* ── BOTTOM ACTION BAR (exact PO style) ── */}
        {activeTab === "company" && (
          <div className="p-3 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 overflow-hidden hover:shadow-md dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-500 dark:text-slate-400">
                <span className="text-red-500">*</span> Required fields must be
                filled
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => onBack()}
                  className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-4 h-[40px] rounded-[9px] transition-all duration-150 text-slate-600 hover:bg-red-50 dark:text-slate-400 dark:hover:bg-slate-800"
                >
                  <X className="w-4 h-4" />
                  <span className="hidden sm:inline">Cancel</span>
                </button>

                <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-1" />

                <CelebrationButton
                  onClick={() => handleSubmit()}
                  label="Submit Company"
                  className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-4 h-[40px] rounded-[9px] transition-all duration-150 bg-emerald-600 hover:bg-emerald-700 text-white"
                  icon={<Send className="w-4 h-4" />}
                  isSuccess={isAnimate}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressForm;
